/**
 * Telemetry — Fachada (Facade) única de captura de erros/eventos.
 *
 * Desacopla TODO o app do fornecedor de crash reporting: o código chama
 * `captureException`/`captureMessage` e nunca importa o SDK do provedor
 * diretamente. Providers (Sentry, Crashlytics, Bugsnag) são plugados em
 * runtime via `registerTelemetryProvider`, mantendo zero acoplamento de import.
 *
 * Sem provider registrado, degrada de forma segura:
 *   - DEV:  espelha no console (visibilidade local).
 *   - PROD: no-op silencioso (nunca lança — telemetria jamais deve derrubar
 *           o caminho que tenta instrumentar).
 *
 * Integração com Sentry (passo de release, fora do bundle JS):
 *   1. `npx expo install @sentry/react-native` + plugin no app.json.
 *   2. Em algum ponto de bootstrap, registrar o adapter:
 *        import * as Sentry from '@sentry/react-native';
 *        Sentry.init({ dsn, tracesSampleRate: 0.1 });
 *        registerTelemetryProvider({
 *          captureException: (e, ctx) => Sentry.captureException(e, { extra: ctx }),
 *          captureMessage:   (m, ctx) => Sentry.captureMessage(m, { extra: ctx }),
 *          setUser:          (u) => Sentry.setUser(u),
 *        });
 *   O restante do app não muda.
 *
 * Contrato de Provider:
 *   { captureException(error, context?), captureMessage?(message, context?), setUser?(user|null) }
 */

const providers = [];
let currentUser = null;

const runSafely = (fn) => {
    try {
        fn();
    } catch {
        // Falha de um provider nunca propaga: telemetria é best-effort.
    }
};

// ── Fila de eventos anteriores ao provider ────────────────────────────────────
// POR QUÊ: na web o SDK do provider é carregado em chunk separado, depois do
// boot (ver `telemetrySentry.js`) — sem esta fila, todo erro entre o início do
// app e a chegada do chunk sumiria em silêncio, e erro de boot é justamente o
// mais valioso. Antes, `captureException` sem provider era no-op em produção.
//
// TETO OBRIGATÓRIO: isto é uma ponte de segundos, não armazenamento. Um erro em
// laço (ex.: dentro de um render) encheria a memória sem limite. Passado o teto,
// os excedentes são descartados — o diagnóstico está nos primeiros, não no
// milésimo. Se nenhum provider chegar (sem DSN), a fila apenas para de crescer.
const PENDING_LIMIT = 50;
const pending = [];

/** Enfileira enquanto não há provider. Retorna true se consumiu o evento. */
const enqueueWhilePending = (entry) => {
    if (providers.length > 0) return false;
    if (pending.length < PENDING_LIMIT) pending.push(entry);
    return true;
};

/** Reenvia ao primeiro provider tudo que foi capturado antes dele existir. */
const flushPendingTo = (provider) => {
    if (pending.length === 0) return;
    const queued = pending.splice(0, pending.length);
    queued.forEach((entry) => runSafely(() => {
        if (entry.kind === 'exception') {
            provider.captureException(entry.error, entry.context);
        } else {
            provider.captureMessage?.(entry.message, entry.context);
        }
    }));
};

/**
 * Há provider ativo? Usado por quem instala handlers globais provisórios (web)
 * para parar de encaminhar assim que o SDK real assume — evitando relatar o
 * mesmo erro duas vezes.
 */
export const hasTelemetryProvider = () => providers.length > 0;

/**
 * Registra um provider e retorna um disposer para removê-lo.
 * Idempotente o suficiente para uso em hot-reload (DEV) sem duplicar envios
 * — o mesmo objeto provider não é registrado duas vezes.
 */
export const registerTelemetryProvider = (provider) => {
    if (!provider || typeof provider.captureException !== 'function') {
        return () => {};
    }
    if (providers.includes(provider)) {
        return () => {
            const i = providers.indexOf(provider);
            if (i >= 0) providers.splice(i, 1);
        };
    }
    providers.push(provider);
    if (currentUser && typeof provider.setUser === 'function') {
        runSafely(() => provider.setUser(currentUser));
    }
    flushPendingTo(provider);
    return () => {
        const i = providers.indexOf(provider);
        if (i >= 0) providers.splice(i, 1);
    };
};

const toError = (value) =>
    value instanceof Error ? value : new Error(typeof value === 'string' ? value : JSON.stringify(value));

export const captureException = (error, context) => {
    const normalized = toError(error);
    if (__DEV__) {
        console.error('[telemetry] exception:', normalized, context ?? '');
    }
    if (enqueueWhilePending({ kind: 'exception', error: normalized, context })) return;
    providers.forEach((p) => runSafely(() => p.captureException(normalized, context)));
};

export const captureMessage = (message, context) => {
    if (__DEV__) {
        console.warn('[telemetry] message:', message, context ?? '');
    }
    if (enqueueWhilePending({ kind: 'message', message, context })) return;
    providers.forEach((p) => runSafely(() => p.captureMessage?.(message, context)));
};

/**
 * Associa o usuário atual aos eventos subsequentes. Aceita `null` no logout.
 * O último usuário é memorizado para reaplicar em providers registrados depois.
 */
export const setTelemetryUser = (user) => {
    currentUser = user;
    providers.forEach((p) => runSafely(() => p.setUser?.(user)));
};

export default {
    registerTelemetryProvider,
    hasTelemetryProvider,
    captureException,
    captureMessage,
    setTelemetryUser,
};
